import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import type { OwnerConnectStatus } from 'shared';
import { Avatar } from '../../../components/Avatar.js';
import { useCan } from '../../../lib/capabilities.js';

export interface OwnerRow {
  id: string;
  displayName: string;
  email: string;
  phone: string | null;
  /** Presigned GET for the owner's most recently uploaded OWNER_PHOTO, or null. */
  photoUrl: string | null;
  currentPropertyCount: number;
  hasPortalAccount: boolean;
  archivedAt: string | null;
  connect: { status: OwnerConnectStatus };
}

function OwnerCard({ row, onOpen }: { row: OwnerRow; onOpen: () => void }) {
  return (
    <div
      className="person-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <Avatar name={row.displayName} photoUrl={row.photoUrl} size="md" />
      <div className="person-card__body">
        <span>
          <strong>{row.displayName}</strong>
          {row.archivedAt ? (
            <>
              {' '}
              <span className="pill">Archived</span>
            </>
          ) : null}
        </span>
        <span className="muted">{row.email}</span>
        <div className="person-card__meta">
          <span className="muted">{row.phone ?? 'No phone on file'}</span>
          <span className="muted">
            {/* "Currently held" rather than "ever held", matching what
                app_owner_can_see_property() scopes on. */}
            {row.currentPropertyCount} propert{row.currentPropertyCount === 1 ? 'y' : 'ies'}
          </span>
          {row.hasPortalAccount ? (
            <span className="pill">Has login</span>
          ) : (
            <span className="muted">No login</span>
          )}
          {/* Only the two states worth a glance at list level — "Not
              invited"/"Invited"/mid-onboarding don't need a badge here,
              same reasoning "No login" stays plain text instead of a pill. */}
          {row.connect.status === 'ENROLLED' ? (
            <span className="pill pill--positive">Direct deposit</span>
          ) : row.connect.status === 'REQUIREMENTS_DUE' ? (
            <span className="pill pill--negative">Payout action needed</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function OwnerList() {
  const navigate = useNavigate();
  const canWrite = useCan('owners:write');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const pageSize = 25;

  const { data, isLoading, error } = useList<OwnerRow>({
    resource: 'owners',
    pagination: { current: page, pageSize },
    filters: [{ field: 'search', operator: 'contains', value: search || undefined }],
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section>
      <div className="row">
        <h1>Owners</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/owners/new')}
          >
            Add owner
          </button>
        ) : null}
      </div>

      <input
        className="field__input"
        type="search"
        placeholder="Search name or email"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />

      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load this list.'}
        </p>
      ) : isLoading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">{search ? `No owners match “${search}”.` : 'No owners yet.'}</p>
      ) : (
        <div className="person-grid">
          {rows.map((row) => (
            <OwnerCard
              key={row.id}
              row={row}
              onOpen={() => void navigate(`/staff/owners/${row.id}/edit`)}
            />
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {page} of {lastPage} · {total} total
          </span>
          <button
            type="button"
            className="button button--quiet"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      ) : null}
    </section>
  );
}
